import React, { useCallback, useEffect, useState } from 'react';
import { Button, Typography, theme } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconMapPin, IconMessage, IconPencil, IconFileInvoice, IconCalendarCheck } from '@tabler/icons-react';
import StaticMap from '../../components/StaticMap';
import { message } from '../../services/toastMessage';
import BackButton from '../../components/BackButton';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import GearLoadingState from '../../components/GearLoadingState';
import { PhotoThumb } from '../../components/quotes/JobPhotos';
import ServiceQuoteModal from '../../components/quotes/ServiceQuoteModal';
import ConfirmVisitModal from '../../components/quotes/ConfirmVisitModal';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useAuth } from '../../contextProviders/AuthProvider';
import {
  acceptServiceRequest,
  cancelServiceRequest,
  declineServiceRequest,
  getServiceRequest,
} from '../../services/ServiceRequestService';
import { IServiceRequest } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import {
  SERVICE_REQUEST_STATUS_ICON,
  SERVICE_REQUEST_STATUS_LABEL_KEYS,
  SERVICE_REQUEST_STATUS_TONES,
  SERVICE_REQUEST_URGENCY_LABEL_KEYS,
} from '../../constants/serviceRequestOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { formatShortDateTime } from '../../helpers/dateFormatHelper';
import { pillToneStyle, SURFACE_1, SURFACE_1_SOFT, TEAL } from '../../theme/creamyTokens';

const { Text, Title, Paragraph } = Typography;

/**
 * One quote request, for whichever of the two is looking.
 *
 * The actions differ by side and by status, and the rule underneath them is
 * the same one the booking negotiation already follows: **you only ever act
 * on what the other party put on the table.** The professional quotes and
 * revises; the customer accepts or declines; either can walk away while there
 * is nothing committed.
 */
const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { userId } = useAuth();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();

  const [request, setRequest] = useState<IServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setRequest(await getServiceRequest(id));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <GearLoadingState />;
  if (!request) return null;

  const isCustomer = request.customerUserId === userId;
  const isProvider = !isCustomer;
  const StatusIcon = SERVICE_REQUEST_STATUS_ICON[request.status];
  const money = (value: number) => formatCurrency(value, request.currencyCode, i18n.language);

  // Whole hours, never below one, and undefined once the clock has run out --
  // a countdown that reads "quedan 0 h" or "-3 h" is worse than no countdown.
  // The sweep closes it within five minutes of that anyway.
  const confirmHoursLeft = request.confirmInSeconds != null && request.confirmInSeconds > 0
    ? Math.max(1, Math.round(request.confirmInSeconds / 3600))
    : undefined;

  const run = async (action: () => Promise<IServiceRequest>, successKey: string) => {
    setWorking(true);
    try {
      setRequest(await action());
      message.success(translate(t, successKey));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_ACTION_ERROR));
    } finally {
      setWorking(false);
    }
  };

  const openChat = () =>
    navigate(`/heavy-duty-machinery/messages?conversationId=${request.conversationId}`);

  return (
    <div style={{ minHeight: '100vh', background: SURFACE_1_SOFT, paddingBottom: 40 }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '12px 16px' : '24px 20px' }}>
        <BackButton fallbackPath="/heavy-duty-machinery/service-requests" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 6 }}>
          <span
            style={{
              ...pillToneStyle(SERVICE_REQUEST_STATUS_TONES[request.status], token),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 12px',
              borderRadius: 999,
              fontSize: 12,
            }}
          >
            <StatusIcon size={12} />
            {translate(t, SERVICE_REQUEST_STATUS_LABEL_KEYS[request.status])}
          </span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {translate(t, SERVICE_REQUEST_URGENCY_LABEL_KEYS[request.urgency])}
          </Text>
        </div>

        <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          {request.serviceTitle || translate(t, TRANSLATIONS.SERVICE_REQUEST_NO_SERVICE_TITLE)}
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {isCustomer ? request.providerDisplayName : request.customerDisplayName}
          {' · '}
          {formatShortDateTime(request.createdAt, i18n.language)}
        </Text>

        {/* The job in their own words, given the room it deserves: it is what
            the whole negotiation is about, and on the professional's side it
            is the only thing they read before deciding. */}
        {/* The summary first, because it is what gets read first -- and the
            customer's own words right under it, never behind a "read more".
            If the professional prices off a tidied-up version that dropped a
            detail, the one who loses money is them. */}
        {request.assistantSummary && (
          <div style={{ background: SURFACE_1, borderRadius: 16, padding: 16, marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_SUMMARY_LABEL)}
            </Text>
            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{request.assistantSummary}</Paragraph>
          </div>
        )}

        <div style={{ background: token.colorBgContainer, borderRadius: 16, padding: 16, marginTop: 12 }}>
          {request.assistantSummary && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_ORIGINAL_LABEL)}
            </Text>
          )}
          <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{request.description}</Paragraph>

          {/* Under the words, where they belong: the photo is what the
              description is pointing at. */}
          {(request.photos ?? []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {request.photos.map((photo) => (
                <PhotoThumb key={photo.id} photo={photo} />
              ))}
            </div>
          )}

          {(request.clarifications ?? []).length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {request.clarifications.map((pair) => (
                <div key={pair.question} style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{pair.question}</Text>
                  <Text style={{ fontSize: 13, display: 'block' }}>{pair.answer}</Text>
                </div>
              ))}
            </div>
          )}
          {(request.city || request.formattedAddress) && (
            <Text type="secondary" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10 }}>
              <IconMapPin size={12} />
              {request.formattedAddress || request.city}
            </Text>
          )}

          {/* Only when the customer picked a real place rather than typing a
              town name. It is the half of "¿dónde es?" a professional cannot
              get from the words: what the drive looks like, and whether it is
              the side of the city they work. Renders nothing without
              coordinates -- see StaticMap. */}
          {request.latitude != null && request.longitude != null && (
            <div style={{ marginTop: 10 }}>
              <StaticMap latitude={request.latitude} longitude={request.longitude} height={150} />
            </div>
          )}
        </div>

        {request.currentAmount != null && (
          <div style={{ background: SURFACE_1, borderRadius: 16, padding: 16, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <Text strong style={{ fontSize: 14 }}>
                {translate(t, TRANSLATIONS.SERVICE_REQUEST_CURRENT_QUOTE)}
              </Text>
              <Text strong style={{ fontSize: 22, color: TEAL, marginLeft: 'auto' }}>
                {money(request.currentAmount)}
              </Text>
            </div>
            {request.proposedFrom && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                {translate(t, TRANSLATIONS.SERVICE_REQUEST_PROPOSED_WINDOW, {
                  from: formatShortDateTime(request.proposedFrom, i18n.language),
                })}
              </Text>
            )}

            {/* Every version, because a price that moved without a reason is
                exactly what a customer will ask about. */}
            {request.quoteHistory.map((quote) => (
              <div key={quote.versionNumber} style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  v{quote.versionNumber} · {money(quote.amount)}
                </Text>
                {quote.reason && (
                  <Text style={{ fontSize: 12, display: 'block', marginTop: 2 }}>{quote.reason}</Text>
                )}
                {quote.lines.map((line, index) => (
                  <div key={index} style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Text style={{ fontSize: 12, flex: 1 }}>
                      {line.title}
                      {line.quantity != null && line.unitPrice != null && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {` · ${line.quantity} ${line.unit ?? ''} × ${money(line.unitPrice)}`}
                        </Text>
                      )}
                    </Text>
                    <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{money(line.amount)}</Text>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {request.closedReason && (
          <div style={{ background: token.colorFillQuaternary, borderRadius: 14, padding: 12, marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{request.closedReason}</Text>
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 20 }}>
          {request.conversationId && (
            <Button icon={<IconMessage size={15} />} onClick={openChat} style={{ borderRadius: 999 }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_OPEN_CHAT)}
            </Button>
          )}

          {isProvider && (request.status === 'Sent' || request.status === 'Quoted') && (
            <Button
              type="primary"
              icon={request.status === 'Sent' ? <IconFileInvoice size={15} /> : <IconPencil size={15} />}
              onClick={() => setQuoteOpen(true)}
              style={{ borderRadius: 999 }}
            >
              {translate(t, request.status === 'Sent' ? TRANSLATIONS.SERVICE_REQUEST_QUOTE_IT : TRANSLATIONS.SERVICE_REQUEST_REQUOTE)}
            </Button>
          )}

          {isProvider && request.status === 'Accepted' && (
            <Button
              type="primary"
              icon={<IconCalendarCheck size={15} />}
              onClick={() => setVisitOpen(true)}
              style={{ borderRadius: 999 }}
            >
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_CONFIRM_VISIT)}
            </Button>
          )}

          {isCustomer && request.status === 'Quoted' && (
            <>
              <Button
                type="primary"
                loading={working}
                onClick={() => run(() => acceptServiceRequest(request.id), TRANSLATIONS.SERVICE_REQUEST_ACCEPTED_TOAST)}
                style={{ borderRadius: 999 }}
              >
                {translate(t, TRANSLATIONS.SERVICE_REQUEST_ACCEPT)}
              </Button>
              <Button onClick={() => setDeclineOpen(true)} style={{ borderRadius: 999 }}>
                {translate(t, TRANSLATIONS.SERVICE_REQUEST_DECLINE)}
              </Button>
            </>
          )}

          {/* Accepted counts: if the professional never fixes the visit, the
              customer would otherwise be stuck holding an agreement nobody is
              acting on. Nothing is reserved or charged until then, so there is
              nothing to unwind. */}
          {isCustomer && ['Sent', 'Quoted', 'Accepted'].includes(request.status) && (
            <Button danger type="text" onClick={() => setCancelOpen(true)} style={{ borderRadius: 999 }}>
              {translate(t, TRANSLATIONS.SERVICE_REQUEST_WITHDRAW)}
            </Button>
          )}
        </div>

        {/* Waiting, and now with an end to the waiting. Saying how long is
            the whole repair: this line used to promise "te avisamos en cuanto
            lo haga" and then say the same thing for ever if they never did. */}
        {isCustomer && request.status === 'Accepted' && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            {confirmHoursLeft != null
              ? translate(t, TRANSLATIONS.SERVICE_REQUEST_WAITING_FOR_VISIT_DEADLINE, { hours: confirmHoursLeft })
              : translate(t, TRANSLATIONS.SERVICE_REQUEST_WAITING_FOR_VISIT)}
          </Text>
        )}

        {/* And the other side of the same clock. They have been accepted --
            this is the reminder that the job is theirs to lose. */}
        {isProvider && request.status === 'Accepted' && confirmHoursLeft != null && (
          <Text style={{ fontSize: 12, display: 'block', marginTop: 12, color: token.colorWarning }}>
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_CONFIRM_DEADLINE, { hours: confirmHoursLeft })}
          </Text>
        )}

        {request.convertedBookingId && (
          <Button
            type="primary"
            block
            onClick={() => navigate(`/heavy-duty-machinery/bookings/${request.convertedBookingId}`)}
            style={{ borderRadius: 999, marginTop: 16, height: 46 }}
          >
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_OPEN_BOOKING)}
          </Button>
        )}
      </div>

      <ServiceQuoteModal
        request={request}
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        onQuoted={(updated) => setRequest(updated)}
      />

      <ConfirmVisitModal
        request={request}
        open={visitOpen}
        onClose={() => setVisitOpen(false)}
        onConfirmed={(bookingId) => navigate(`/heavy-duty-machinery/bookings/${bookingId}`)}
      />

      <ConfirmBottomSheet
        open={declineOpen}
        title={translate(t, TRANSLATIONS.SERVICE_REQUEST_DECLINE_CONFIRM_TITLE)}
        description={translate(t, TRANSLATIONS.SERVICE_REQUEST_DECLINE_CONFIRM_BODY)}
        confirmText={translate(t, TRANSLATIONS.SERVICE_REQUEST_DECLINE)}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        onClose={() => setDeclineOpen(false)}
        onConfirm={() => {
          setDeclineOpen(false);
          run(() => declineServiceRequest(request.id), TRANSLATIONS.SERVICE_REQUEST_DECLINED_TOAST);
        }}
      />

      <ConfirmBottomSheet
        open={cancelOpen}
        title={translate(t, TRANSLATIONS.SERVICE_REQUEST_WITHDRAW_CONFIRM_TITLE)}
        description={translate(t, TRANSLATIONS.SERVICE_REQUEST_WITHDRAW_CONFIRM_BODY)}
        confirmText={translate(t, TRANSLATIONS.SERVICE_REQUEST_WITHDRAW)}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        onClose={() => setCancelOpen(false)}
        onConfirm={() => {
          setCancelOpen(false);
          run(() => cancelServiceRequest(request.id), TRANSLATIONS.SERVICE_REQUEST_WITHDRAWN_TOAST);
        }}
      />
    </div>
  );
};

export default ServiceRequestDetail;
